import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpenCheck, FileSpreadsheet, FileText, ListChecks, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useCuadernoCampoEnabled } from "@/hooks/useCuadernoCampoEnabled";
import { CUADERNO_JUNTA_KEY, useCuadernoJunta } from "@/hooks/useCuadernoJunta";
import { BorradorCambiado, congelarVersion, type FormatoCuaderno } from "@/lib/cuaderno/congelar";
import type { AvisoGuarda, MotivoGuarda } from "@/lib/cuaderno/guardaSalida";
import { deliverPdfBlob } from "@/lib/pdfDelivery";
import { deliverXlsx } from "@/lib/reportXlsx";
import { CuadernoRevisionSheet } from "./CuadernoRevisionSheet";
import type { SeleccionCuaderno } from "@/lib/cuaderno/seleccion";

export interface AssistantCuadernoCardProps {
  year: number;
  /** null = el titular de la empresa (D14). */
  titularLocationId: string | null;
  /**
   * El borrador que vio el asistente (`cuaderno_generar`), solo como pista: el
   * borrador se busca y se abre SIEMPRE por (empresa, titular, año), con
   * `cargarBorrador` para leer y `ensure_cuaderno_draft` para congelar.
   */
  draftId?: string | null;
  /**
   * El cuaderno de una SELECCIÓN (`cuaderno_seleccion`, 28-09-2026): solo esas
   * líneas y sus valores. Se descarga sin congelar versión (la del año es la del
   * cuaderno entero) y sin tocar el borrador.
   */
  seleccion?: SeleccionCuaderno | null;
}

const COLOR = "#1F6F5C";
const PDF_COLOR = "#E5574E";
const XLSX_COLOR = "#1D7A46";

const fechaCorta = (iso: string | null | undefined) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");

/**
 * EL CUADERNO DE EXPLOTACIÓN EN EL CHAT (CQ4a) — y el mismo, tal cual, detrás
 * del botón «Modelo de la Junta (v9)» de /spray-logs.
 *
 * Enseña el resumen (filas del 3.1, huecos, cuántas filas salen completas) y
 * tres botones: PDF sobre la plantilla de la Junta (v9), Excel con las mismas
 * secciones y la hoja de revisión (de dónde sale cada dato; no se imprime).
 *
 * Al descargar:
 *   1. GUARDA DE SALIDA: si un nº de registro no existe en el MAPA o una dosis o
 *      una superficie no cuadran con el cálculo, NO se genera y se dice por qué;
 *   2. se genera en el navegador (pdf-lib / SheetJS, cargados al pulsar);
 *   3. se CONGELA la versión (fichero en `cuaderno-exports` + fila con su sha256)
 *      y solo entonces se entrega: una versión que se firma queda guardada;
 *   4. se entrega con `deliverPdfBlob` / `deliverXlsx` (en la app nativa, al
 *      visor del sistema).
 * Y avisa si los datos han cambiado desde la última versión descargada.
 *
 * Nunca dice «oficial» ni «cumple la normativa»: «Modelo de la Junta (v9)».
 */
export function AssistantCuadernoCard({ year, titularLocationId, seleccion = null }: AssistantCuadernoCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const interruptor = useCuadernoCampoEnabled();
  const q = useCuadernoJunta({ year, titularLocationId, enabled: interruptor.allowed, seleccion });
  const [ocupado, setOcupado] = useState<FormatoCuaderno | null>(null);
  const [revisionAbierta, setRevisionAbierta] = useState(false);

  if (!interruptor.loading && !interruptor.allowed) {
    return (
      <div data-testid="assistant-cuaderno-card" className="mt-2 w-full rounded-2xl border bg-white px-3 py-2.5 text-[12px] text-muted-foreground shadow-sm">
        {t("cuadernoJunta.notAvailable")}
      </div>
    );
  }

  const d = q.data;
  const filas = d?.build.data["3.1"]?.length ?? 0;
  const huecos = d?.build.huecos.length ?? 0;
  const cobertura = d?.build.cobertura;
  const guardaOk = d?.guarda.ok ?? false;
  const cambiado = d?.ultima ? d.ultima.data_sha256 !== d.dataSha256 : null;
  const titular = d?.build.data.cabecera?.explotacion ?? null;

  const descargar = async (formato: FormatoCuaderno) => {
    if (!d || ocupado || !guardaOk) return;
    setOcupado(formato);
    try {
      const pdf = await import("@/lib/cuaderno/cuadernoPdf");
      let bytes: Uint8Array | Blob;
      if (formato === "pdf") bytes = await pdf.generateCuadernoPdf(d.build.data);
      else bytes = await (await import("@/lib/cuaderno/cuadernoXlsx")).buildCuadernoXlsx(d.build.data);
      const nombre0 = pdf.cuadernoFilename(d.build.data, formato);
      const nombre = seleccion ? nombre0.replace(/(\.[a-z]+)$/i, "-seleccion$1") : nombre0;

      // Una selección se entrega sin congelar: no es la versión del año.
      if (seleccion) {
        if (formato === "pdf") await deliverPdfBlob(bytes as Uint8Array, nombre);
        else await deliverXlsx(bytes as Blob, nombre);
        return;
      }

      // Primero se congela; si no se puede guardar, no se entrega.
      const v = await congelarVersion(supabase, {
        companyId: q.companyId,
        titularLocationId,
        year,
        // El borrador leído por (empresa, titular, año); si aún no hay, `ensure_cuaderno_draft` lo abre.
        draftId: d.borrador?.id ?? null,
        // M4: la versión del borrador con la que se montó `d.build.data` (sin borrador, 0).
        draftVersion: d.borrador?.version ?? 0,
        formato,
        bytes,
        data: d.build.data,
        cobertura: d.build.cobertura,
        modelo: formato === "pdf" ? pdf.CUADERNO_MODELO : null,
        alcanceFincas: q.alcanceFincas,
      });

      if (formato === "pdf") await deliverPdfBlob(bytes as Uint8Array, nombre);
      else await deliverXlsx(bytes as Blob, nombre);
      toast.success(t("cuadernoJunta.saved", { version: v.version }));
      void queryClient.invalidateQueries({ queryKey: [CUADERNO_JUNTA_KEY] });
    } catch (e) {
      console.error("[AssistantCuadernoCard] descarga:", e);
      if (e instanceof BorradorCambiado) {
        // Otro «Aplicar» cambió el borrador: no se entrega; se recalcula y se vuelve a pulsar.
        toast.error(t("cuadernoJunta.draftChanged"));
        void queryClient.invalidateQueries({ queryKey: [CUADERNO_JUNTA_KEY] });
      } else toast.error(t("cuadernoJunta.downloadError"));
    } finally {
      setOcupado(null);
    }
  };

  const pill =
    "flex-1 min-h-[38px] inline-flex items-center justify-center gap-1.5 rounded-xl border px-2.5 text-[12.5px] font-semibold transition-all active:scale-[0.98] disabled:opacity-50";

  return (
    <>
      <div data-testid="assistant-cuaderno-card" className="mt-2 w-full rounded-2xl border border-[#b6dded] bg-white px-3 py-2.5 shadow-sm">
        <div className="flex items-center gap-3">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{ backgroundColor: COLOR, boxShadow: `0 6px 14px ${COLOR}42, inset 0 1px 0 rgba(255,255,255,0.28)` }}
          >
            <BookOpenCheck className="h-5 w-5 text-white" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-bold text-foreground">{t("cuadernoJunta.title", { year })}</span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {t("cuadernoJunta.model")}
              {titular ? ` · ${titular}` : ""}
            </span>
            {seleccion && (
              <span data-testid="assistant-cuaderno-selection" className="block truncate text-[11px] font-semibold" style={{ color: COLOR }}>
                {seleccion.titulo ? t("cuadernoJunta.selectionNamed", { name: seleccion.titulo }) : t("cuadernoJunta.selection")}
              </span>
            )}
          </span>
        </div>

        {(q.isLoading || interruptor.loading) && !d && (
          <p className="mt-2 flex items-center gap-1.5 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t("cuadernoJunta.loading")}
          </p>
        )}

        {q.isError && (
          <div className="mt-2 flex items-center justify-between gap-2 text-[12px] text-destructive">
            <span>{t("cuadernoJunta.loadError")}</span>
            <button type="button" onClick={() => void q.refetch()} className="inline-flex items-center gap-1 font-semibold">
              <RefreshCw className="h-3.5 w-3.5" />
              {t("cuadernoJunta.retry")}
            </button>
          </div>
        )}

        {d && (
          <>
            <div data-testid="assistant-cuaderno-summary" className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
              <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">{t("cuadernoJunta.rows", { n: filas })}</span>
              <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">{t("cuadernoJunta.gaps", { n: huecos })}</span>
              {cobertura && cobertura.filas > 0 && (
                <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">
                  {t("cuadernoJunta.coverage", { done: cobertura.filasCompletas, total: cobertura.filas })}
                </span>
              )}
            </div>
            {filas === 0 && <p className="mt-1.5 text-[12px] text-muted-foreground">{t("cuadernoJunta.empty", { year })}</p>}
            {q.acotado && <p className="mt-1.5 text-[11px] text-muted-foreground">{t("cuadernoJunta.scoped")}</p>}
            <p data-testid="assistant-cuaderno-version" className="mt-1.5 text-[11px] text-muted-foreground">
              {seleccion
                ? t("cuadernoJunta.selectionNote")
                : d.ultima
                ? cambiado
                  ? t("cuadernoJunta.changedSince", { version: d.ultima.version, date: fechaCorta(d.ultima.generated_at) })
                  : t("cuadernoJunta.sameAs", { version: d.ultima.version, date: fechaCorta(d.ultima.generated_at) })
                : t("cuadernoJunta.noVersions")}
            </p>

            {!guardaOk && (
              <div data-testid="assistant-cuaderno-guard" role="alert" className="mt-2 rounded-xl border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-[12px] text-destructive">
                <p className="font-semibold">{t("cuadernoJunta.guard.title")}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {d.guarda.motivos.slice(0, 6).map((m, i) => (
                    <li key={i}>{textoGuarda(t, m)}</li>
                  ))}
                </ul>
                {d.guarda.motivos.length > 6 && <p className="mt-1">{t("cuadernoJunta.guard.more", { n: d.guarda.motivos.length - 6 })}</p>}
              </div>
            )}

            {/* CU9: lo que decidió la persona NO bloquea: se avisa y se genera igual. */}
            {guardaOk && (d.guarda.avisos?.length ?? 0) > 0 && (
              <div data-testid="assistant-cuaderno-notices" className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12px] text-amber-900">
                <p className="font-semibold">{t("cuadernoJunta.guard.noticesTitle")}</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {d.guarda.avisos.slice(0, 6).map((a, i) => (
                    <li key={i}>{textoAviso(t, a)}</li>
                  ))}
                </ul>
                {d.guarda.avisos.length > 6 && <p className="mt-1">{t("cuadernoJunta.guard.more", { n: d.guarda.avisos.length - 6 })}</p>}
              </div>
            )}

            <div className="mt-2.5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void descargar("pdf")}
                disabled={!guardaOk || !!ocupado}
                aria-busy={ocupado === "pdf"}
                className={pill}
                style={{ borderColor: `${PDF_COLOR}55`, backgroundColor: `${PDF_COLOR}0F`, color: "#B93C34" }}
              >
                {ocupado === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {t("cuadernoJunta.downloadPdf")}
              </button>
              <button
                type="button"
                onClick={() => void descargar("xlsx")}
                disabled={!guardaOk || !!ocupado}
                aria-busy={ocupado === "xlsx"}
                className={pill}
                style={{ borderColor: `${XLSX_COLOR}55`, backgroundColor: `${XLSX_COLOR}0F`, color: XLSX_COLOR }}
              >
                {ocupado === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                {t("cuadernoJunta.downloadExcel")}
              </button>
              <button
                type="button"
                onClick={() => setRevisionAbierta(true)}
                className={pill}
                style={{ borderColor: `${COLOR}55`, backgroundColor: `${COLOR}0F`, color: COLOR }}
              >
                <ListChecks className="h-4 w-4" />
                {t("cuadernoJunta.review")}
              </button>
            </div>
          </>
        )}
      </div>
      {d && <CuadernoRevisionSheet open={revisionAbierta} onOpenChange={setRevisionAbierta} revision={d.build.revision} />}
    </>
  );
}

function textoGuarda(t: TFunction, m: MotivoGuarda): string {
  if (m.tipo === "filas_desalineadas") return t("cuadernoJunta.guard.filas_desalineadas");
  // Regla 4 (A2): sin fila (la línea no está en el 3.1).
  if (m.tipo === "fitosanitario_fuera") return t("cuadernoJunta.guard.fitosanitario_fuera", { product: m.producto });
  const fila = m.fila + 1;
  if (m.tipo === "dosis_no_cuadra" || m.tipo === "superficie_no_cuadra") {
    return t(`cuadernoJunta.guard.${m.tipo}`, { row: fila, product: m.producto, printed: m.impresa, computed: m.calculada });
  }
  return t(`cuadernoJunta.guard.${m.tipo}`, { row: fila, product: m.producto, number: m.numero });
}

/** CU9: el texto de un aviso de la guarda (no bloquea). */
function textoAviso(t: TFunction, a: AvisoGuarda): string {
  if (a.tipo === "fitosanitario_quitado") return t("cuadernoJunta.guard.fitosanitario_quitado", { product: a.producto });
  const fila = a.fila + 1;
  if (a.tipo === "linea_sin_parte") return t("cuadernoJunta.guard.linea_sin_parte", { row: fila, product: a.producto });
  if (a.tipo === "dosis_distinta_del_parte" || a.tipo === "superficie_distinta_del_parte") {
    return t(`cuadernoJunta.guard.${a.tipo}`, { row: fila, product: a.producto, printed: a.impresa, fromRecord: a.delParte });
  }
  return t(`cuadernoJunta.guard.${a.tipo}`, { row: fila, product: a.producto, number: a.numero });
}
